import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronRightIcon from '@mui/icons-material/ChevronLeft';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import DrawerToRender from './drawerToRender';
import GuardianDrawerToRender from './guardianDrawerToRender';
import APP_LOGO from './../../../assets/images/app-logo.png';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { MenuItem, Select, useMediaQuery, useTheme } from '@mui/material';
import { useRbacActions, ACTION_TYPE } from 'src/context/rbac.context';
import {
  mockRbacAdmin,
  mockRbacFinance,
  mockRbacTeacher,
  mockRbacClassTeacher,
  mockRbacAdminStaff,
  mockRbacParent,
} from 'src/__mocks__/rback';
import { InfoOutlined } from '@mui/icons-material';
import type { IRbac } from 'src/apis/__shared/rbac.interfaces';
import { navigationDrawerNestedSx, navigationDrawerPaperSx } from 'src/design-system/muiColorsOverrideHandler';
import isAppWithMock from 'src/utils/isAppWithMock';
import { useBrandingContext } from 'src/context/branding.context';

const drawerWidth = 300;
const mobileToolbarHeight = 56;

/** Dev role switcher: maps UI value → RBAC fixture (only applied when REACT_APP_WITH_MOCK). */
const MOCK_ROLE_TO_RBAC: Record<string, IRbac> = {
  admin: mockRbacAdmin,
  finance: mockRbacFinance,
  teacher: mockRbacTeacher,
  classTeacher: mockRbacClassTeacher,
  adminStaff: mockRbacAdminStaff,
  parent: mockRbacParent,
};

type NavigationDrawerProps = {
  children: React.ReactNode;
  selectedPage: string;
  window: Window & typeof globalThis;
  isGuardian?: boolean;
};

const NavigationDrawer = (props: NavigationDrawerProps) => {
  const { children, selectedPage, window, isGuardian = false } = props;
  const { translateText } = useTranslation();
  const { dispatch } = useRbacActions();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [open, setOpen] = useState(!isMobile);
  const [selectedRole, setSelectedRole] = useState<string>('admin');

  const { logoUrl: brandingLogoUrl } = useBrandingContext();
  const logoSrc = brandingLogoUrl || APP_LOGO;
  useEffect(() => {
    if (isMobile) {
      setOpen(false);
      setMobileOpen(false);
    } else {
      setOpen(true);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!isAppWithMock()) return;
    const rbacPayload = MOCK_ROLE_TO_RBAC[selectedRole];
    if (rbacPayload) {
      dispatch({ type: ACTION_TYPE.SET_RBAC, payload: rbacPayload });
    }
  }, [selectedRole, dispatch]);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setOpen(!open);
    }
  };

  const handleDrawerClose = () => {
    if (isMobile) {
      setMobileOpen(false);
    } else {
      setOpen(false);
    }
  };

  const container =
    window !== undefined ? () => window.document.body : undefined;

  const drawerContent = (
    <>
      <Box
        id="drawerHeader"
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          padding: theme.spacing(0, 1),
          ...theme.mixins.toolbar,
        })}
      >
        {open || isMobile ? (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flex: 1,
              }}
              data-testid="drawer-logo-container"
            >
              <Box
                component="img"
                src={logoSrc}
                alt="App Logo"
                sx={{
                  height: 35,
                  width: 'auto',
                  display: 'block',
                }}
                data-testid="drawer-logo"
              />
              <Typography
                variant="h6"
                data-testid="app-main"
                sx={{
                  color: '#FFFFFF',
                  fontWeight: 600,
                  lineHeight: 1.2,
                  margin: 0,
                }}
              >
                {translateText('navigation.appName')}
              </Typography>
            </Box>
            <IconButton
              aria-label={translateText('navigation.closeDrawer')}
              onClick={handleDrawerClose}
              edge="start"
              sx={{
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.06)',
                },
              }}
              data-testid="drawer-close-button"
            >
              <MenuIcon />
            </IconButton>
          </Box>
        ) : (
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.06)',
              },
            }}
            data-testid="drawer-open-button"
          >
            <ChevronRightIcon />
          </IconButton>
        )}
      </Box>
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />
      {isAppWithMock() && (open || isMobile) && !isGuardian && (
        <Box sx={{ p: 2 }} data-testid="role-selector-container">
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <InfoOutlined
              sx={{ color: 'rgba(168, 176, 194, 0.72)' }}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(168, 176, 194, 0.72)',
                mb: 1,
                display: 'block',
                fontSize: '0.75rem',
                whiteSpace: 'pre-wrap',
              }}
              data-testid="role-selector-note"
            >
              NOTE: This is to toggle between the different possible roles and
              understand possibilities.
            </Typography>
          </Box>
          <Select
            value={selectedRole}
            onChange={(event) => {
              setSelectedRole(event.target.value as string);
            }}
            fullWidth
            sx={(theme) => ({
              backgroundColor: theme.palette.common.white,
              '& .MuiSelect-select': {
                color: theme.palette.text.primary,
              },
            })}
            data-testid="role-selector"
          >
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="finance">Finance</MenuItem>
            <MenuItem value="teacher">Teacher</MenuItem>
            <MenuItem value="classTeacher">Class Teacher</MenuItem>
            <MenuItem value="adminStaff">Admin Staff</MenuItem>
            {/* <MenuItem value="parent">Parent</MenuItem> */}
          </Select>
        </Box>
      )}
      <Box sx={navigationDrawerNestedSx} data-testid="navigation-drawer-nav-lists">
        {isGuardian ? (
          <GuardianDrawerToRender selectedPage={selectedPage} isMenuOpen={open || isMobile} />
        ) : (
          <DrawerToRender selectedPage={selectedPage} isMenuOpen={open || isMobile} />
        )}
      </Box>
    </>
  );

  return (
    <Box
      sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}
      data-testid="navigation-drawer"
    >
      <CssBaseline />
      {isMobile && (
        <AppBar
          position="fixed"
          color="inherit"
          elevation={0}
          sx={{
            width: { lg: `calc(100% - ${drawerWidth}px)` },
            ml: { lg: `${drawerWidth}px` },
            display: { lg: 'none' },
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            boxShadow: 'none',
            borderBottom: `1px solid ${theme.palette.divider}`,
            borderRadius: 0,
          }}
          data-testid="mobile-app-bar"
        >
          <Toolbar
            sx={{ minHeight: mobileToolbarHeight }}
            data-testid="mobile-app-toolbar"
          >
            <IconButton
              color="inherit"
              aria-label={translateText('navigation.openDrawer')}
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
              data-testid="mobile-menu-button"
            >
              <MenuIcon />
            </IconButton>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flex: 1,
              }}
              data-testid="mobile-logo-container"
            >
              <Box
                component="img"
                src={logoSrc}
                alt="App Logo"
                sx={{
                  height: 35,
                  width: 'auto',
                  display: 'block',
                }}
                data-testid="mobile-logo"
              />
              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{
                  color: 'text.primary',
                  fontWeight: 600,
                  lineHeight: 1.2,
                }}
                data-testid="mobile-app-title"
              >
                {translateText('navigation.appName')}
              </Typography>
            </Box>
          </Toolbar>
        </AppBar>
      )}
      <Box
        component="nav"
        sx={{ width: { lg: isMobile ? 0 : open ? drawerWidth : `calc(${theme.spacing(16)} + 1px)` }, flexShrink: { lg: 0 } }}
        aria-label="navigation menu"
        data-testid="navigation-drawer-nav"
      >
        <MuiDrawer
          {...(isMobile && container ? { container } : {})}
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : open}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', lg: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              ...navigationDrawerPaperSx,
              width: isMobile ? drawerWidth : open ? drawerWidth : `calc(${theme.spacing(16)} + 1px)`,
              borderRadius: '0 !important',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.easeInOut,
                duration: theme.transitions.duration.enteringScreen,
              }),
              '@media (max-width: 600px)': {
                borderRadius: '0 !important',
              },
            },
            '& .MuiBackdrop-root': {
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              transition: 'opacity 0.2s ease-in-out',
            },
          }}
          PaperProps={{
            sx: {
              ...navigationDrawerPaperSx,
              borderRadius: '0 !important',
              '@media (max-width: 600px)': {
                borderRadius: '0 !important',
              },
            },
          }}
          data-testid="navigation-drawer-paper"
        >
          {drawerContent}
        </MuiDrawer>
      </Box>
      <Box
        component="main"
        data-testid="navigation-drawer-main"
        sx={{
          flexGrow: 1,
          width: {
            xs: '100%',
            lg: open ? `calc(100% - ${drawerWidth}px)` : `calc(100% - ${theme.spacing(8)} - 1px)`,
          },
          height: {
            xs: `calc(100% - ${mobileToolbarHeight}px)`,
            lg: '100%',
          },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.leavingScreen,
          }),
          mt: { xs: `${mobileToolbarHeight}px`, lg: 0 },
          animation: 'fadeInContent 0.4s ease-out',
          '@keyframes fadeInContent': {
            '0%': {
              opacity: 0,
            },
            '100%': {
              opacity: 1,
            },
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default NavigationDrawer;
